<template>
  <div id="coupon-products" class="flex flex-col gap-4">
    <div class="p-3 border rounded-md">
      <div class="flex flex-wrap items-center gap-2">
        <span class="font-semibold mr-auto">Bulk edit with Excel</span>
        <button
          type="button"
          class="btn btn-dark-button h-10"
          :disabled="downloading"
          @click="downloadExcel"
        >
          {{ downloading ? "Downloading..." : "Download Excel" }}
        </button>
        <button
          type="button"
          class="btn btn-info-button h-10"
          :disabled="uploading"
          @click="$refs.excelInput.click()"
        >
          Upload Excel
        </button>
        <input
          ref="excelInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="hidden"
          @change="onExcelSelected"
        />
      </div>
      <p class="text-xs text-gray-500 mt-1">
        Download the file (it has the current products and an Instructions
        sheet), add or change rows, and upload it back. Columns: nkr_code,
        discount_type (percentage, amount, fixed_final_price) and amount.
      </p>

      <div v-if="excelFile" class="mt-3 p-3 rounded-md bg-gray-50 text-sm">
        <div class="flex items-center gap-2 mb-2">
          <b class="truncate">{{ excelFile.name }}</b>
          <span v-if="uploading" class="text-gray-500">Checking…</span>
        </div>
        <label class="flex items-center gap-2 mb-2">
          <input
            v-model="removeMissing"
            type="checkbox"
            :disabled="uploading"
            @change="previewExcel"
          />
          Remove products not in the file (the file is the complete list)
        </label>
        <template v-if="excelSummary">
          <div class="flex flex-wrap gap-2 mb-2">
            <span class="px-2 py-1 rounded bg-green-100 text-green-800">
              {{ excelSummary.added }} new
            </span>
            <span class="px-2 py-1 rounded bg-blue-100 text-blue-800">
              {{ excelSummary.updated }} updated
            </span>
            <span class="px-2 py-1 rounded bg-gray-200 text-gray-700">
              {{ excelSummary.unchanged }} unchanged
            </span>
            <span
              v-if="excelSummary.removed"
              class="px-2 py-1 rounded bg-red-100 text-red-800"
            >
              {{ excelSummary.removed }} removed
            </span>
          </div>
          <div v-if="excelSummary.errors.length" class="mb-2">
            <p class="text-red-700 font-semibold mb-1">
              Fix these rows and upload the file again (nothing was saved):
            </p>
            <ul class="max-h-48 overflow-y-auto text-red-700">
              <li v-for="error in excelSummary.errors" :key="error.row">
                Row {{ error.row }}
                <b v-if="error.nkrCode" class="font-mono">{{
                  error.nkrCode
                }}</b>
                : {{ error.errors.join(", ") }}
              </li>
            </ul>
          </div>
        </template>
        <div class="flex gap-2">
          <button
            type="button"
            class="btn btn-success-button"
            :disabled="!canApplyExcel"
            @click="applyExcel"
          >
            Apply changes
          </button>
          <button type="button" class="btn btn-dark-button" @click="clearExcel">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <form
      class="rule-form grid gap-3 items-end p-3 border rounded-md bg-gray-50"
      @submit.prevent="save"
    >
      <div class="relative flex flex-col my-1">
        <label class="text-sm">Product</label>
        <div
          v-if="form.nkrCode"
          class="flex items-center justify-between gap-2 bordered bg-white px-2 h-10 rounded"
        >
          <span class="truncate">
            <b class="font-mono">{{ form.nkrCode }}</b> {{ form.productName }}
          </span>
          <button
            v-if="!editingId"
            type="button"
            class="text-gray-500 hover:text-red-600"
            @click="clearProduct"
          >
            &times;
          </button>
        </div>
        <template v-else>
          <input
            v-model="search"
            type="text"
            class="regular bordered h-10"
            placeholder="Search by NKR code (4+ characters)"
            @input="searchPart"
          />
          <div
            v-if="options.length > 0"
            class="absolute top-full left-0 right-0 z-10 bg-white border rounded shadow-lg max-h-72 overflow-y-auto"
          >
            <button
              v-for="option in options"
              :key="option.id"
              type="button"
              class="w-full text-left p-2 hover:bg-gray-100 border-b"
              @click="selectOption(option)"
            >
              <b class="font-mono">{{ option.nkrCode }}</b>
              {{ option.customTitle }}
            </button>
          </div>
          <span v-if="searching" class="text-xs text-gray-500">Searching…</span>
        </template>
      </div>
      <BaseSelectField
        v-model="form.discountType"
        :options="ruleTypes"
        label="Discount"
      />
      <BaseTextField
        v-model="form.amount"
        type="number"
        step="0.01"
        :label="form.discountType === 'percentage' ? 'Percent (%)' : 'Amount ($)'"
      />
      <div class="flex gap-2 my-1">
        <button
          type="submit"
          class="btn btn-success-button h-10"
          :disabled="!canSave || saving"
        >
          {{ editingId ? "Save" : "Add" }}
        </button>
        <button
          v-if="editingId"
          type="button"
          class="btn btn-dark-button h-10"
          @click="resetForm"
        >
          Cancel
        </button>
      </div>
    </form>

    <div v-if="loading"><BaseSmallSpinner /></div>
    <div
      v-else-if="products.length === 0"
      class="border border-dashed rounded-md p-6 text-center text-gray-500"
    >
      No products yet. A product rule wins over any category rule that also
      covers it.
    </div>
    <table v-else class="w-full text-sm">
      <thead>
        <tr class="text-left text-gray-500 border-b">
          <th class="py-2">Code</th>
          <th class="py-2">Product</th>
          <th class="py-2">Discount</th>
          <th class="py-2"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="product in products"
          :key="product.id"
          class="border-b hover:bg-gray-50"
          :class="{ 'bg-yellow-50': product.id === editingId }"
        >
          <td class="py-2 font-mono font-medium">{{ product.productNkr }}</td>
          <td class="py-2">{{ product.productName }}</td>
          <td class="py-2">
            <span class="px-2 py-1 rounded bg-green-50 text-green-800">
              {{ ruleText(product) }}
            </span>
          </td>
          <td class="py-2 text-right whitespace-nowrap">
            <template v-if="confirmDeleteId === product.id">
              <button
                class="text-red-600 font-semibold mr-2"
                @click="deleteProduct(product)"
              >
                Delete
              </button>
              <button class="text-gray-600" @click="confirmDeleteId = null">
                Cancel
              </button>
            </template>
            <template v-else>
              <button class="text-blue-600 mr-3" @click="edit(product)">
                Edit
              </button>
              <button
                class="text-red-600"
                @click="confirmDeleteId = product.id"
              >
                Remove
              </button>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script>
import { debounce } from "lodash";
import { getProducts } from "@/api/store/ProductsService.js";
import {
  getCouponProducts,
  addProductToCoupon,
  updateProductFromCoupon,
  deleteProductFromCoupon,
  downloadCouponProductsExcel,
  uploadCouponProductsExcel
} from "@/api/store/CouponsService";
import { RULE_DISCOUNT_TYPES, ruleDiscountText } from "./couponLabels.js";

const emptyForm = () => ({
  nkrCode: "",
  productName: "",
  discountType: "percentage",
  amount: ""
});

export default {
  name: "CouponProducts",
  props: {
    id: {
      type: String,
      required: true
    },
    code: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      downloading: false,
      uploading: false,
      excelFile: null,
      excelSummary: null,
      removeMissing: false,
      products: [],
      loading: true,
      saving: false,
      search: "",
      searching: false,
      options: [],
      searchCallId: 0,
      editingId: null,
      confirmDeleteId: null,
      form: emptyForm()
    };
  },
  computed: {
    canApplyExcel() {
      const summary = this.excelSummary;
      return (
        !this.uploading &&
        summary &&
        summary.errors.length === 0 &&
        summary.added + summary.updated + summary.removed > 0
      );
    },
    ruleTypes() {
      return RULE_DISCOUNT_TYPES;
    },
    canSave() {
      return (
        this.form.nkrCode &&
        this.form.discountType &&
        this.form.amount !== "" &&
        Number(this.form.amount) >= 0
      );
    }
  },
  created() {
    this.runSearch = debounce(this.runSearch, 500);
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    async downloadExcel() {
      this.downloading = true;
      try {
        await downloadCouponProductsExcel(this.id, this.code);
      } catch (e) {
        this.$toast.error("Error downloading the file");
      } finally {
        this.downloading = false;
      }
    },
    onExcelSelected(event) {
      const file = event.target.files[0];
      // permite volver a elegir el mismo archivo después de corregirlo
      event.target.value = "";
      if (!file) return;
      this.excelFile = file;
      this.previewExcel();
    },
    // Primero sólo se valida (apply=false) para mostrar el resumen
    async previewExcel() {
      if (!this.excelFile) return;
      this.uploading = true;
      this.excelSummary = null;
      try {
        this.excelSummary = await uploadCouponProductsExcel(
          this.id,
          this.excelFile,
          { apply: false, removeMissing: this.removeMissing }
        );
      } catch (e) {
        this.$toast.error(
          e.response && e.response.data ? e.response.data : e.message
        );
        this.clearExcel();
      } finally {
        this.uploading = false;
      }
    },
    async applyExcel() {
      this.uploading = true;
      try {
        const summary = await uploadCouponProductsExcel(
          this.id,
          this.excelFile,
          { apply: true, removeMissing: this.removeMissing }
        );
        if (!summary.applied) {
          this.excelSummary = summary;
          this.$toast.error("The file has errors, nothing was saved");
          return;
        }
        this.$toast.success(
          `Products saved: ${summary.added} new, ${summary.updated} updated` +
            (summary.removed ? `, ${summary.removed} removed` : "")
        );
        this.clearExcel();
        this.loadProducts();
        this.$emit("changed");
      } catch (e) {
        this.$toast.error(
          e.response && e.response.data ? e.response.data : e.message
        );
      } finally {
        this.uploading = false;
      }
    },
    clearExcel() {
      this.excelFile = null;
      this.excelSummary = null;
      this.removeMissing = false;
    },
    ruleText(product) {
      return ruleDiscountText(product);
    },
    loadProducts() {
      this.loading = this.products.length === 0;
      getCouponProducts(this.id)
        .then(res => {
          this.products = res;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchPart() {
      const code = this.search.replaceAll(" ", "");
      if (code.length <= 3) {
        this.options = [];
        return;
      }
      this.runSearch(code);
    },
    runSearch(code) {
      const callId = ++this.searchCallId;
      this.searching = true;
      getProducts({
        storeEnabled: "enabled",
        page: 1,
        limitPerPage: "10",
        orderCol: "nkr_code",
        orderDir: "asc",
        search: code,
        requestPrice: true,
        priceLevel: "online"
      })
        .then(res => {
          if (callId === this.searchCallId) this.options = res.products;
        })
        .finally(() => {
          if (callId === this.searchCallId) this.searching = false;
        });
    },
    selectOption(option) {
      if (this.products.some(p => p.productNkr === option.nkrCode)) {
        this.$toast.error(`${option.nkrCode} is already in the coupon`);
        return;
      }
      this.form.nkrCode = option.nkrCode;
      this.form.productName = option.customTitle;
      this.search = "";
      this.options = [];
    },
    clearProduct() {
      this.form.nkrCode = "";
      this.form.productName = "";
    },
    resetForm() {
      this.editingId = null;
      this.form = emptyForm();
    },
    edit(product) {
      this.editingId = product.id;
      this.form = {
        nkrCode: product.productNkr,
        productName: product.productName,
        discountType: product.discountType,
        amount: product.amount
      };
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        if (this.editingId) {
          await updateProductFromCoupon(this.id, this.editingId, this.form);
          this.$toast.success("Product updated");
        } else {
          await addProductToCoupon(this.id, this.form);
          this.$toast.success("Product added");
        }
        this.resetForm();
        this.loadProducts();
        this.$emit("changed");
      } catch (e) {
        this.$toast.error(
          e.response && e.response.data ? e.response.data : e.message
        );
      } finally {
        this.saving = false;
      }
    },
    async deleteProduct(product) {
      try {
        await deleteProductFromCoupon(this.id, product.id);
        this.$toast.success("Product removed");
        if (this.editingId === product.id) this.resetForm();
        this.loadProducts();
        this.$emit("changed");
      } catch (e) {
        this.$toast.error("Error removing product");
      } finally {
        this.confirmDeleteId = null;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.rule-form {
  grid-template-columns: minmax(0, 3fr) minmax(0, 1.5fr) minmax(0, 1fr) auto;
  > * {
    min-width: 0;
  }
  ::v-deep select,
  ::v-deep input {
    width: 100%;
    min-width: 0;
  }
}
</style>
